import { useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useMemo } from 'react';

import { EWidgetRegion } from '#shared';

import { useAppDispatch } from '../../redux-store';
import { regionActions, useSelectRegionsEnabled } from '../../slices';

export const layoutOptions: EWidgetRegion[] = [
  EWidgetRegion.Left,
  EWidgetRegion.Center,
  EWidgetRegion.Right,
];

export const useRegionToggle = () => {
  const dispatch = useAppDispatch();
  const { breakpoints } = useTheme();
  const enabledRegions = useSelectRegionsEnabled().map(region => region.id);
  const isBigDevice = useMediaQuery(breakpoints.up('xl'));

  // Calculate max allowed regions based on screen size
  const maxRegions = useMemo(() => (isBigDevice ? 3 : 2), [isBigDevice]);

  const toggleRegion = useCallback(
    (option: EWidgetRegion) => {
      const isCurrentlyEnabled = enabledRegions.includes(option);

      if (isCurrentlyEnabled) {
        // Prevent disabling the last region
        if (enabledRegions.length <= 1) {
          return;
        }

        // Disable this region
        const nextRegions = enabledRegions.filter(id => id !== option);

        dispatch(
          regionActions.regionEnable({ regionId: nextRegions, only: true }),
        );
      } else {
        // Enable this region
        const nextRegions = [...enabledRegions, option];

        if (nextRegions.length <= maxRegions) {
          dispatch(
            regionActions.regionEnable({ regionId: nextRegions, only: true }),
          );
        }
      }
    },
    [enabledRegions, dispatch, maxRegions],
  );

  const getOptionState = useCallback(
    (option: EWidgetRegion) => {
      const isEnabled = enabledRegions.includes(option);
      const isLastEnabled = isEnabled && enabledRegions.length === 1;
      const maxReached = !isEnabled && enabledRegions.length >= maxRegions;

      let tooltip = '';
      let disabled = false;

      if (isLastEnabled) {
        tooltip = 'At least one region must remain active';
        disabled = true;
      } else if (maxReached) {
        tooltip = `Maximum ${maxRegions} columns at this screen size`;
        disabled = true;
      }

      return { disabled, tooltip };
    },
    [enabledRegions, maxRegions],
  );

  return {
    enabledRegions,
    toggleRegion,
    maxRegions,
    layoutOptions,
    getOptionState,
  };
};
